import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import UnifiedDiffView from './UnifiedDiffView';
import {parseCommitDiff, prettyModel, trailerValue} from '../utils/commitDiffParser';
import {keySourceShort} from './dashboard/aiProvenanceLabels';
import './CommitDiffView.css';

// One commit as vfs-service's GET /history/:sha/diff returns it (`git show` text), rendered: a header (message,
// author by name, time, the AI trailers as small badges) above the files' diff (UnifiedDiffView).

const SHOWN_TRAILERS = new Set(['via', 'ai-model', 'ai-key', 'ai-playbook', 'ai-agent']);

function badgesOf(commit) {
  const badges = [];
  const model = trailerValue(commit, 'AI-Model');
  if (model) { badges.push({key: 'model', label: prettyModel(model), title: `Model: ${model}`}); }
  const key = trailerValue(commit, 'AI-Key');
  if (key && key !== 'none') { badges.push({key: 'key', label: keySourceShort(key), title: `Key: ${key}`}); }
  const playbook = trailerValue(commit, 'AI-Playbook');
  if (playbook) { badges.push({key: 'playbook', label: playbook.replace(/@[^@]*$/, ''), title: `Playbook: ${playbook}`}); }
  const agent = trailerValue(commit, 'AI-Agent');
  if (agent) { badges.push({key: 'agent', label: agent, title: `Agent: ${agent}`}); }
  const via = trailerValue(commit, 'Via');
  if (via) { badges.push({key: 'via', label: `via ${via}`, title: `Via: ${via}`}); }
  return badges;
}

function formatDate(raw) {
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? raw : new Date(time).toLocaleString();
}

export default function CommitDiffView({diffText, resolveActor = null, actorId = null, timestamp = null, formatTime = null, maxRows}) {
  const {commit, files} = useMemo(() => parseCommitDiff(diffText), [diffText]);

  if (!commit && files.length === 0) {
    // Not a diff at all (an old server's message…): as it came.
    return diffText && diffText.trim()
      ? <pre className="cdv-raw">{diffText}</pre>
      : <div className="udv-empty">No changes to show.</div>;
  }

  const author = resolveActor ? resolveActor(actorId || commit?.author) : null;
  const authorName = author ? author.name : (commit?.author || actorId || '');
  const authorTitle = author ? [author.email, author.id].filter(Boolean).join(' · ') : commit?.authorEmail;
  const when = timestamp || commit?.date || '';
  const badges = commit ? badgesOf(commit) : [];
  const otherTrailers = (commit?.trailers || []).filter((t) => !SHOWN_TRAILERS.has(t.key.toLowerCase()));

  return (
    <div className="cdv-root">
      {commit && (
        <header className="cdv-header">
          <div className="cdv-subject">{commit.subject || '(no message)'}</div>
          {commit.body && <div className="cdv-body">{commit.body}</div>}
          <div className="cdv-meta">
            <span className="cdv-author" title={authorTitle || undefined}>{authorName}</span>
            {when && (
              <time className="cdv-time" dateTime={timestamp || undefined} title={formatDate(when)}>
                {formatTime ? formatTime(when) : formatDate(when)}
              </time>
            )}
            <span className="cdv-sha" title={commit.sha}>{commit.sha.slice(0, 10)}</span>
          </div>
          {(badges.length > 0 || otherTrailers.length > 0) && (
            <ul aria-label="Commit details" className="cdv-badges">
              {badges.map((badge) => (
                <li className={`cdv-badge cdv-badge-${badge.key}`} key={badge.key} title={badge.title}>{badge.label}</li>
              ))}
              {otherTrailers.map((t) => (
                <li className="cdv-badge" key={`t:${t.key}`} title={`${t.key}: ${t.value}`}>{`${t.key}: ${t.value}`}</li>
              ))}
            </ul>
          )}
        </header>
      )}
      <UnifiedDiffView files={files} maxRows={maxRows} />
    </div>
  );
}

CommitDiffView.propTypes = {
  actorId: PropTypes.string,
  diffText: PropTypes.string,
  formatTime: PropTypes.func,
  maxRows: PropTypes.number,
  resolveActor: PropTypes.func,
  timestamp: PropTypes.string,
};
